import React, {
    useState,
    useEffect,
    useCallback,
    memo,
    useRef,
    FocusEvent,
} from 'react'
import {
    Box,
    Button,
    Grid,
    Typography,
    Menu,
    MenuItem,
    Fade,
    TextField,
    styled,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { DateRange } from 'react-date-range'
import { translate } from '../i18n'
import { useDates } from './AnyDateRangeHook'
import {
    AnyDateRangeProps,
    DateRangeValue,
    DateRangeItemNames,
    DateRangeSelection,
} from './AnyDateRange.types'
import { es } from 'date-fns/locale'
import { PICKER_DATE_FORMAT, formatDate } from '../components'
import { AnyComponentRef } from './AnyComponent.types'
import { TEXT_REQUIRED_VALIDATOR } from '../validators'

const defaultMenuItemNames: DateRangeItemNames[] = [
    'today',
    'yesterday',
    'this_week',
    'last_week',
    'this_month',
    'last_month',
    'custom',
]

const ThemedDateRange = styled(Box)(({ theme }) => {
    const bg1 =
        theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.common.white
    const bg2 =
        theme.palette.mode === 'dark'
            ? theme.palette.grey[700]
            : theme.palette.grey[100]
    const bg3 =
        theme.palette.mode === 'dark'
            ? theme.palette.grey[900]
            : theme.palette.common.white
    const disabled =
        theme.palette.mode === 'dark'
            ? theme.palette.grey[600]
            : theme.palette.grey[400]

    return {
        '.ThemedDateRange': {
            backgroundColor: bg1,
            '& .rdrDateDisplayWrapper': {
                backgroundColor: bg2,
            },
            '& .rdrDateDisplayWrapper .rdrDateDisplay': {
                color: `${
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.main
                        : theme.palette.primary.main
                } !important`,
            },
            '& .rdrDateDisplayWrapper .rdrDateDisplay .rdrDateInput': {
                backgroundColor: bg3,
            },
            '& .rdrDateDisplayWrapper .rdrDateDisplay .rdrDateInput input': {
                color: theme.palette.text.primary,
            },
            '& .rdrMonthAndYearWrapper .rdrMonthAndYearPickers select': {
                color: theme.palette.text.primary,
                background: bg1,
            },
            '& .rdrMonthAndYearWrapper .rdrMonthAndYearPickers select:hover': {
                background: bg2,
            },
            '& .rdrMonthAndYearWrapper button': {
                backgroundColor: bg2,
                color: theme.palette.text.primary,
            },
            '& .rdrMonths .rdrWeekDays .rdrWeekDay': {
                color: theme.palette.text.primary,
            },
            '& .rdrMonths .rdrDay .rdrDayNumber span': {
                color: theme.palette.text.primary,
            },
            '& .rdrMonths .rdrDay.rdrDayPassive .rdrDayNumber span': {
                color: disabled,
            },
        },
    }
})

function AnyDateRange({
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    defaultFormat = PICKER_DATE_FORMAT,
    locale = es,
    fullWidth = true,
    name,
    label,
    initialValue,
    required,
    disabled,
    minDate,
    maxDate,
    register,
    notifier,
    menuItemsCustom: menuItems = defaultMenuItemNames,
}: AnyDateRangeProps) {
    const [dates, maskStartDate, maskEndDate] = useDates()
    const {
        today,
        yesterday,
        thisMonthStart,
        thisMonthEnd,
        lastMonthStart,
        lastMonthEnd,
        thisWeekStart,
        thisWeekEnd,
        lastWeekStart,
        lastWeekEnd,
    } = dates

    const formatValue = useCallback(
        (startDate?: Date, endDate?: Date): string => {
            return `${formatDate({
                date: startDate,
                pattern: defaultFormat,
                locale,
            })} - ${formatDate({
                date: endDate,
                pattern: defaultFormat,
                locale,
            })}`
        },
        [defaultFormat, locale]
    )

    const [blured, setBlured] = useState(false)
    const [valid, setValid] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState<string>()
    const [value, setValue] = useState<DateRangeValue>()
    const [selected, setSelected] = useState<DateRangeItemNames>('today')
    const [dateRanges, setDateRanges] = useState<string>('')
    const [gridAnchorEl, setGridAnchorEl] = useState<null | HTMLElement>(null)
    const [selectionRange, setSelectionRange] = useState<DateRangeSelection>({
        key: 'selection',
        startDate: maskStartDate(today),
        endDate: maskEndDate(today),
    })

    const ref = useRef<AnyComponentRef<DateRangeValue | undefined>>({
        valid,
        dirty,
        value,
        setBlur: () => setBlured(true),
    })

    const setValues = useCallback(
        (start?: Date, end?: Date) => {
            if (start || end) {
                const startDate = start ? maskStartDate(start) : undefined
                const endDate = end ? maskEndDate(end) : undefined
                setSelectionRange((curr) => ({ ...curr, startDate, endDate }))
            } else {
                setSelectionRange((curr) => ({
                    ...curr,
                    startDate: undefined,
                    endDate: undefined,
                }))
            }
        },
        [maskStartDate, maskEndDate]
    )

    const onClosePopup = useCallback(() => {
        setGridAnchorEl(null)
    }, [])

    const onAccept = useCallback(() => {
        if (selectionRange.startDate && selectionRange.endDate) {
            onClosePopup()
            setDirty(true)
            setDateRanges(
                formatValue(selectionRange.startDate, selectionRange.endDate)
            )
            setValue(selectionRange as DateRangeValue)
        }
    }, [selectionRange, formatValue, onClosePopup])

    const onShowPopup = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (disabled) return

            setGridAnchorEl(event.currentTarget)
            if (!selectionRange.startDate && !selectionRange.endDate) {
                setValues(today, today)
                setSelected('today')
            }
        },
        [disabled, selectionRange, today, setValues]
    )

    const onClean = useCallback(() => {
        onClosePopup()
        setValues()
        setValue(undefined)
        setDirty(false)
        setDateRanges('')
        setSelected('custom')
    }, [setValues, onClosePopup])

    const onChangeRange = useCallback(
        (ranges: any) => {
            setSelected('custom')
            setValues(ranges.selection.startDate, ranges.selection.endDate)
        },
        [setValues]
    )

    const selectRange = (selected: DateRangeItemNames) => {
        setSelected(selected)
        switch (selected) {
            case 'today':
                setValues(today, today)
                break
            case 'yesterday':
                setValues(yesterday, yesterday)
                break
            case 'this_week':
                setValues(thisWeekStart, thisWeekEnd)
                break
            case 'last_week':
                setValues(lastWeekStart, lastWeekEnd)
                break
            case 'this_month':
                setValues(thisMonthStart, thisMonthEnd)
                break
            case 'last_month':
                setValues(lastMonthStart, lastMonthEnd)
                break
            default:
        }
    }

    const inputBlured = useCallback(
        (_event: FocusEvent<HTMLInputElement>) => {
            if (!blured) {
                setBlured(true)
            }
        },
        [blured]
    )

    const onNotify = useCallback(
        (value?: DateRangeValue) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    useEffect(() => {
        const valid =
            !required || (!!value && !!value.startDate && !!value.endDate)
        const error = valid ? undefined : TEXT_REQUIRED_VALIDATOR.getMessage()

        ref.current.valid = valid
        ref.current.dirty = dirty
        ref.current.value = value

        setValid(valid)
        setError(error)

        onNotify(value)
    }, [value, dirty, required, onNotify])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    useEffect(() => {
        if (initialValue) {
            setSelected('custom')
            setDateRanges(
                formatValue(initialValue.startDate, initialValue.endDate)
            )
            setSelectionRange((curr) => ({
                ...curr,
                startDate: maskStartDate(initialValue.startDate ?? today),
                endDate: maskEndDate(initialValue.endDate ?? today),
            }))
            setValue(initialValue)
        }
    }, [initialValue, formatValue, maskEndDate, maskStartDate, today])

    return (
        <Grid container>
            <TextField
                variant={variant}
                size={size}
                margin={margin}
                name={name}
                label={label}
                value={dateRanges}
                required={required}
                disabled={disabled}
                fullWidth={fullWidth}
                error={blured && !valid}
                helperText={blured ? error : undefined}
                onBlur={inputBlured}
                onClick={onShowPopup}
                InputProps={{
                    readOnly: true,
                    endAdornment: <ArrowDropDownIcon sx={{ mr: -0.6 }} />,
                }}
            />
            {gridAnchorEl && (
                <Menu
                    id="date-range-item"
                    anchorEl={gridAnchorEl}
                    keepMounted
                    onClose={onClosePopup}
                    open
                    TransitionComponent={Fade}
                >
                    <Grid container spacing={1} sx={{ p: 1 }}>
                        <Grid item xs={12} sm={4}>
                            {menuItems.map((item) => (
                                <MenuItem
                                    key={item}
                                    onClick={() => selectRange(item)}
                                    selected={item === selected}
                                    dense
                                >
                                    <Typography
                                        variant="inherit"
                                        color={
                                            selected === item
                                                ? 'primary'
                                                : 'inherit'
                                        }
                                    >
                                        {
                                            translate(
                                                `date_ranges.${item}`
                                            ) as string
                                        }
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <ThemedDateRange pl={1} pt={1}>
                                <DateRange
                                    ranges={[selectionRange]}
                                    onChange={onChangeRange}
                                    locale={locale}
                                    maxDate={maxDate}
                                    minDate={minDate}
                                    weekStartsOn={1}
                                    className="ThemedDateRange"
                                />
                            </ThemedDateRange>
                        </Grid>
                        <Grid item xs={12}>
                            <Box px={1}>
                                <Grid container justifyContent="space-between">
                                    <Grid item xs="auto">
                                        <Button
                                            onClick={onClean}
                                            variant="outlined"
                                            color="inherit"
                                        >
                                            {translate('buttons.clear')}
                                        </Button>
                                    </Grid>
                                    <Grid item xs="auto">
                                        <Button
                                            onClick={onAccept}
                                            variant="contained"
                                            color="primary"
                                        >
                                            {translate('buttons.accept')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Menu>
            )}
        </Grid>
    )
}

export default memo(AnyDateRange)
